import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { enrichErrorWithObservability } from "@/lib/monitoring/sentry";
import { toast } from "@/lib/toast";
import { Error } from "../types";

/**
 * Custom hook to manage the global application error state.
 */
export const useGlobalErrorManager = () => {
  const [error, setError] = useState<Error | undefined>();

  const setGlobalError = useCallback<Dispatch<SetStateAction<Error | undefined>>>((value) => {
    setError((previousError) => {
      const nextError = typeof value === "function" ? value(previousError) : value;
      return nextError ? enrichErrorWithObservability(nextError) : undefined;
    });
  }, []);

  useEffect(() => {
    if (!error) {
      return;
    }

    const hasLocationPath = Boolean(error.location?.path);

    if (!hasLocationPath && error.showToast !== false) {
      toast.error(error.title || "Ein Fehler ist aufgetreten", {
        id: `${error.statusCode}-${error.traceId ?? error.correlationId ?? error.message}`,
        description: error.message,
        error,
        persistent: true,
      });
    }

    if (!error.location?.redirect) {
      setError(undefined);
    }
  }, [error]);

  return {
    error,
    setError: setGlobalError,
  };
};
